import { useState, useRef, useEffect } from 'react';
import { Plus, GripVertical, X, Info } from 'lucide-react';
import { getIconComponent } from '../../lib/icons';
import defaultData from '../../data/defaultExpenseTypes.json';
import { theme } from '../../lib/theme';
import { useTranslations } from '../../i18n';
import { SupportedLanguage, normalizeExpenseTypeKey, isServiceType, SERVICE_SUBTYPES, SERVICE_GROUP_KEY } from '../../lib/expenseTypeHelpers';

type CarExpenseTypesEditorProps = {
    selectedTypes: string[];
    onChange: (types: string[]) => void;
};

type ExpenseTypeData = {
    icon: string;
    color: string;
    translations: Record<string, string>;
    descriptions?: Record<string, string>;
    is_deprecated?: boolean;
    service_group?: boolean;
};

// Whether any service subtype is in the selected list
function anyServiceSelected(selected: string[]): boolean {
    return SERVICE_SUBTYPES.some(k => selected.includes(k));
}

export function CarExpenseTypesEditor({ selectedTypes, onChange }: CarExpenseTypesEditorProps) {
    const { t, language } = useTranslations();

    const currentLanguage = language as SupportedLanguage;

    const serviceEnabled = anyServiceSelected(selectedTypes);

    // Compute active items for display (grouping service types under one virtual key)
    const displayItems: string[] = [];
    for (const key of selectedTypes) {
        if (isServiceType(normalizeExpenseTypeKey(key))) {
            if (!displayItems.includes(SERVICE_GROUP_KEY)) {
                displayItems.push(SERVICE_GROUP_KEY);
            }
        } else {
            displayItems.push(key);
        }
    }

    // Pool of available types (not yet selected)
    const allTypeKeys = Object.keys(defaultData);
    const availableItems: string[] = [];
    if (!serviceEnabled) {
        availableItems.push(SERVICE_GROUP_KEY);
    }
    allTypeKeys.forEach(key => {
        const item = (defaultData as any)[key] as ExpenseTypeData;
        if (!item.is_deprecated && !item.service_group && !selectedTypes.includes(key)) {
            availableItems.push(key);
        }
    });

    const [draggedItem, setDraggedItem] = useState<string | null>(null);
    const [dragOverItem, setDragOverItem] = useState<string | null>(null);
    const [tooltipKey, setTooltipKey] = useState<string | null>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    // Close tooltip on click outside or scroll
    useEffect(() => {
        function handleDismiss() {
            setTooltipKey(null);
        }

        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            const isTrigger = target instanceof Element && (target.closest('[data-tooltip-trigger]') !== null);
            
            if (tooltipRef.current && !tooltipRef.current.contains(target) && !isTrigger) {
                handleDismiss();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        // Using capture true to catch scroll events from any scrollable parent
        window.addEventListener('scroll', handleDismiss, true);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleDismiss, true);
        };
    }, []);

    const getTypeName = (key: string) => {
        if (key === SERVICE_GROUP_KEY) return serviceGroupLabel;
        const normalizedKey = normalizeExpenseTypeKey(key);
        const item = (defaultData as any)[normalizedKey] as ExpenseTypeData | undefined;
        return item?.translations[currentLanguage] || item?.translations['en'] || key;
    };

    const getTypeDescription = (key: string) => {
        if (key === SERVICE_GROUP_KEY) {
            return (
                <div className="space-y-3">
                    {SERVICE_SUBTYPES.map(subtype => {
                        const item = (defaultData as any)[subtype] as ExpenseTypeData | undefined;
                        const name = item?.translations[currentLanguage] || item?.translations['en'] || subtype;
                        const desc = item?.descriptions?.[currentLanguage] || item?.descriptions?.['en'];
                        if (!desc) return null;
                        return (
                            <div key={subtype} className="space-y-0.5">
                                <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item?.color }} />
                                    {name}
                                </div>
                                <div className="text-[11px] opacity-80 pl-3.5 border-l border-gray-100 dark:border-white/5">
                                    {desc}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        const normalizedKey = normalizeExpenseTypeKey(key);
        const item = (defaultData as any)[normalizedKey] as ExpenseTypeData | undefined;
        return item?.descriptions?.[currentLanguage] || item?.descriptions?.['en'] || null;
    };

    const serviceGroupLabel = (t.expenses as any).serviceGroup?.name || 'Service';
    const serviceSubtypeNames = SERVICE_SUBTYPES
        .map(k => {
            const item = (defaultData as any)[k] as ExpenseTypeData | undefined;
            return item?.translations[currentLanguage] || item?.translations['en'] || k;
        })
        .join(', ');

    const handleAdd = (key: string) => {
        if (key === SERVICE_GROUP_KEY) {
            onChange([...selectedTypes, ...SERVICE_SUBTYPES]);
        } else {
            onChange([...selectedTypes, key]);
        }
    };

    const handleRemove = (key: string) => {
        if (key === SERVICE_GROUP_KEY) {
            onChange(selectedTypes.filter(t => !isServiceType(t)));
        } else {
            onChange(selectedTypes.filter(t => t !== key));
        }
    };

    const handleDragStart = (e: React.DragEvent, index: number) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
        setDraggedItem(displayItems[index]);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        setDragOverItem(displayItems[index]);
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'));
        if (isNaN(sourceIndex)) return;

        const newDisplayItems = [...displayItems];
        const [movedItem] = newDisplayItems.splice(sourceIndex, 1);
        newDisplayItems.splice(targetIndex, 0, movedItem);

        // Convert display items back to the raw selectedTypes array
        const newTypes: string[] = [];
        for (const item of newDisplayItems) {
            if (item === SERVICE_GROUP_KEY) {
                newTypes.push(...SERVICE_SUBTYPES);
            } else {
                newTypes.push(item);
            }
        }
        onChange(newTypes);
        setDraggedItem(null);
        setDragOverItem(null);
    };

    return (
        <div className="space-y-6">
            {/* Active (sortable) types */}
            <div className="space-y-3">
                {displayItems.length > 0 && (
                    <div className="flex items-center justify-end">
                        <span className="text-[10px] uppercase font-bold text-gray-400">{t.cars.dragToReorder}</span>
                    </div>
                )}

                <div className="space-y-2">
                    {displayItems.map((key, index) => {
                        const isServiceGroup = key === SERVICE_GROUP_KEY;
                        const isDragging = draggedItem === key;
                        const isDragOver = dragOverItem === key;

                        // For non-service items, fetch standard icon and color
                        let IconComponent;
                        let itemColor = '#6B7280';
                        let itemName = key;
                        let itemSubtitle = '';

                        if (isServiceGroup) {
                            IconComponent = getIconComponent('wrench');
                            itemColor = '#34D399';
                            itemName = serviceGroupLabel;
                            itemSubtitle = serviceSubtypeNames;
                        } else {
                            const normalizedKey = normalizeExpenseTypeKey(key);
                            const item = (defaultData as any)[normalizedKey] as ExpenseTypeData;
                            if (item) {
                                IconComponent = getIconComponent(item.icon);
                                itemColor = item.color;
                                itemName = getTypeName(key);
                            } else {
                                IconComponent = getIconComponent('circle');
                            }
                        }

                        return (
                            <div
                                key={key}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={() => { setDraggedItem(null); setDragOverItem(null); }}
                                className={`flex items-center justify-between p-3 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-white/10 transition-all ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'border-blue-500 ring-1 ring-blue-500' : ''}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="cursor-grab text-gray-400 hover:text-gray-600">
                                        <GripVertical size={18} />
                                    </div>
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                                        style={{ backgroundColor: `${itemColor}15` }}
                                    >
                                        <IconComponent size={20} style={{ color: itemColor }} />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="font-bold text-gray-900 dark:text-white">
                                            {itemName}
                                        </span>
                                        {itemSubtitle && (
                                            <span className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 mt-0.5 max-w-[150px] sm:max-w-none truncate sm:overflow-visible sm:whitespace-normal">
                                                {itemSubtitle}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {getTypeDescription(key) && (
                                        <div className="relative">
                                            <button
                                                type="button"
                                                data-tooltip-trigger="true"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setTooltipKey(tooltipKey === key ? null : key);
                                                }}
                                                className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                            >
                                                <Info size={18} />
                                            </button>
                                            {tooltipKey === key && (
                                                <div 
                                                    ref={tooltipRef}
                                                    onClick={() => setTooltipKey(null)}
                                                    className="absolute top-1/2 -translate-y-1/2 right-full mr-3 w-64 p-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-white/10 z-[100] text-xs leading-relaxed text-gray-600 dark:text-gray-300 pointer-events-auto cursor-pointer animate-in fade-in zoom-in-95 slide-in-from-right-2 duration-200"
                                                >
                                                    <div className="font-bold text-gray-900 dark:text-white mb-1">{getTypeName(key)}</div>
                                                    {getTypeDescription(key)}
                                                    <div className="absolute top-1/2 -translate-y-1/2 left-full w-3 h-3 bg-white dark:bg-slate-800 border-r border-t border-gray-200 dark:border-white/10 rotate-45 -ml-1.5" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(key)}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Available types (Add) */}
            {availableItems.length > 0 && (
                <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-white/10">
                    <label className={theme.typography.sectionLabel}>
                        {t.cars.availableTypes}
                    </label>
                    <div className="space-y-2">
                        {availableItems.map(key => {
                            const isServiceGroup = key === SERVICE_GROUP_KEY;

                            let IconComponent;
                            let itemColor = '#6B7280';
                            let itemName = key;
                            let itemSubtitle = '';

                            if (isServiceGroup) {
                                IconComponent = getIconComponent('wrench');
                                itemColor = '#34D399';
                                itemName = serviceGroupLabel;
                                itemSubtitle = serviceSubtypeNames;
                            } else {
                                const item = (defaultData as any)[key] as ExpenseTypeData;
                                if (!item) return null;
                                IconComponent = getIconComponent(item.icon);
                                itemColor = item.color;
                                itemName = getTypeName(key);
                            }

                            return (
                                <div
                                    key={key}
                                    onClick={() => handleAdd(key)}
                                    className="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-white/5 hover:bg-green-50 dark:hover:bg-green-900/10 hover:border-green-200 dark:hover:border-green-800 transition-all group text-left cursor-pointer"
                                >
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center opacity-70 group-hover:opacity-100 shrink-0"
                                        style={{ backgroundColor: `${itemColor}15` }}
                                    >
                                        <IconComponent size={20} style={{ color: itemColor }} />
                                    </div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className="font-bold text-gray-600 dark:text-gray-400 group-hover:text-green-700 dark:group-hover:text-green-400">
                                            {itemName}
                                        </span>
                                        {itemSubtitle && (
                                            <span className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                                {itemSubtitle}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        {getTypeDescription(key) && (
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    data-tooltip-trigger="true"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTooltipKey(tooltipKey === key ? null : key);
                                                    }}
                                                    className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors opacity-70 group-hover:opacity-100"
                                                >
                                                    <Info size={18} />
                                                </button>
                                                {tooltipKey === key && (
                                                    <div 
                                                        ref={tooltipRef}
                                                        onClick={() => setTooltipKey(null)}
                                                        className="absolute top-1/2 -translate-y-1/2 right-full mr-3 w-64 p-3 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-white/10 z-[100] text-xs leading-relaxed text-gray-600 dark:text-gray-300 pointer-events-auto cursor-pointer animate-in fade-in zoom-in-95 slide-in-from-right-2 duration-200"
                                                    >
                                                        <div className="font-bold text-gray-900 dark:text-white mb-1">{getTypeName(key)}</div>
                                                        {getTypeDescription(key)}
                                                        <div className="absolute top-1/2 -translate-y-1/2 left-full w-3 h-3 bg-white dark:bg-slate-800 border-r border-t border-gray-200 dark:border-white/10 rotate-45 -ml-1.5" />
                                                    </div>
                                                )}

                                            </div>
                                        )}
                                        <Plus size={18} className="text-gray-400 group-hover:text-green-500 shrink-0 mr-2" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
